/**
 * Glue layer between game state, Rive layer, and minimal DOM controls.
 * Mirrors the imperative side-effect orchestration in `_GameScreenState`
 * of packages/amgcarracing_v2/lib/presentation/screens/game_screen.dart.
 *
 * Car Racing differs from Dragon Tiger in several structural ways:
 *
 *   - ONE Rive artboard ("Main" / "MainStageMachine") with everything
 *     baked in: bet areas, chip picker (also DOM-mirrored), Play/Clear
 *     buttons, race animation, ending highlight.
 *   - No card reveal — the race is a single `Spin` Number input (1..32),
 *     with each winning betItemId mapping to 4 possible final positions.
 *   - No `BetPanel` codes — winner highlight uses `Winning = 378 - id`.
 *   - 8 bet areas (G* + S* × {Lamborghini, Ferrari, Benz, BMW}).
 *   - Rive events: BetButton (1-based id), evtClearClick, evtButtonPlayClick,
 *     spinEnd, evtSoundOnOff, evtMusicOnOff. Chip-value changes come via a
 *     `chipsAmt` property on any of those events.
 *   - Per-area bet totals are surfaced through Rive text runs
 *     (txtSubTotal1..8 for self, txtAllTotal1..8 for all players).
 */
import type { BetType, GameRound } from '../types/game';
import {
  ALL_BETS,
  BET_ITEM_IDS,
  betTypeFromRiveBetId,
  labelOf,
} from '../types/game';
import {
  DEFAULT_AGENT_ID,
  DEFAULT_GAME_CONFIG,
  DEFAULT_PASSWORD,
  ENDING_ANIMATION_DELAY_MS,
  FINALIZE_AFTER_BET_SINGLE_MS,
  FINALIZE_AFTER_SPIN_END_MS,
  FINALIZE_AFTER_START_PLAY_MS,
  GAME_KEY_MULTI,
  GAME_KEY_SINGLE,
  SPIN_INITIAL_DELAY_SINGLE_MS,
  SPIN_MAPPING,
  SPIN_RESET_GAP_MS,
  WINNING_HIDE_DELAY_MS,
} from '../config';
import type { Api } from '../net/api';
import { ApiError } from '../net/api';
import type { GameState, GameStateSnapshot } from './gameState';
import type { RiveLayer } from '../rive/riveLayer';
import type { Hud } from '../ui/hud';
import type { TableControls } from '../ui/controls';
import type { UrlParams } from './urlParams';
import type { MultiplayerHub, HubEvent } from '../net/multiplayerHub';
// showModeSelectionDialog is no longer called — the mode is derived from the
// launch URL (see urlParams.resolveGameMode). Kept as a type-only import for
// GameMode; the value import would trip noUnusedLocals. Restore the value
// import to re-enable the launch dialog.
import { type GameMode } from '../ui/modeSelectionDialog';
import type { GameTimerWidget } from '../ui/timerWidget';
import { parseResultString } from './resultParser';
import { GameLogManager } from './gameLogManager';
import { debugLog, error, warn } from '../util/logger';

/**
 * Multiplayer only: end the visible bet countdown this many seconds before
 * the server's real betting-window close. The batched bet submission fires
 * when the countdown reaches 0, so this buffer gives it time to reach the
 * backend before betting closes and the race starts.
 */
const BET_SUBMIT_BUFFER_SEC = 2;

interface OrchestratorDeps {
  state: GameState;
  api: Api;
  rive: RiveLayer;
  hud: Hud;
  controls: TableControls;
  hub: MultiplayerHub;
  timer: GameTimerWidget;
  params: UrlParams;
  onInteractive?: () => void;
}

export class GameOrchestrator {
  private readonly d: OrchestratorDeps;
  private gid: string = '';

  // Result-sequence guards (per round).
  private didStartRace = false;
  private didFinalizeRound = false;
  // The history-road entry is appended once per round, at the result reveal
  // (spinEnd) — NOT when the round data arrives at race start — so the road
  // never spoils the winner before the race ends. Guards that single append.
  private didRecordHistory = false;
  // Multiplayer Game-Log snapshot. The settlement Result can land >6.5 s after
  // spinEnd, by which point finalizeRound() has already run resetForNewRound()
  // — nulling currentRound AND clearing the bets. So we snapshot the round
  // (winner/id) and the wagered total/details at StartPlay (while they're still
  // alive) and log from these in handleResult, independent of currentRound.
  private pendingLogRound: GameRound | null = null;
  private pendingLogBetTotal = 0;
  private pendingLogBetDetails = '';
  private didLogRound = false;
  // Balance settlement is deferred to the win/lose reveal (the winner
  // highlight + result amount, +1500 ms after spinEnd) so the displayed
  // (available) balance stays "bet deducted" through the race and only
  // reflects winnings once the result is shown.
  private pendingSettleBalance: number | null = null;
  private didSettle = false;
  // Multiplayer: the result reveal reached the settle point but the server's
  // Result event (which carries the post-round balance) hasn't arrived yet.
  // We hold the bets in place until it does (see applySettlement) so the
  // displayed available balance never flashes the pre-settlement wallet.
  private awaitingSettleBalance = false;
  // Bumped at every round start; settlement captures it before the async balance
  // fetch and bails if a new round started meanwhile (stale fetch must not settle
  // into the next round's wallet).
  private roundToken = 0;
  // Guards applyResultSettlement to once per round (a retransmitted settlement
  // Result must not recompute against the already-settled wallet / double-log).
  private didApplyResultSettlement = false;
  // Multiplayer bets are NOT sent per-chip; they're batched and submitted
  // when the bet countdown ends, so the player can still clear beforehand.
  private didSubmitMpBets = false;
  // YOU WIN / YOU LOSE popup (MainWinLose overlay) guards. The popup is shown
  // exactly once per round at the ending reveal. `endingRevealDone` records
  // that the post-race reveal point has been reached, so a late multiplayer
  // Result (which patches in the real win/lose AFTER the reveal ran with a
  // still-zero amount) can drive the popup as a backstop.
  private didShowWinLose = false;
  private endingRevealDone = false;
  private sequenceTimers: number[] = [];

  // Multiplayer-specific timers.
  private betCountdownHandle: number | null = null;
  private restartCountdownHandle: number | null = null;
  private lastMpEventIndex: number | null = null;

  constructor(deps: OrchestratorDeps) {
    this.d = deps;
  }

  async start(): Promise<void> {
    this.d.state.on((next, prev) => this.onStateChange(next, prev));
    this.bindRiveEvents();
    this.bindHubEvents();
    this.d.controls.bindChips(this.d.state.config);
    this.d.controls.setSelectedChip(this.d.state.selectedChipIndex);
    this.d.controls.setControlsEnabled(false, false);

    this.d.hud.setUserName(this.d.params.userName);
    this.d.state.setPhase('initializing');
    this.d.hud.setStatus('Connecting…');

    try {
      await this.signIn();
      await this.loadConfigAndBalance();
      this.d.onInteractive?.();

      // Mode selection dialog disabled — the mode is now derived from the
      // launch URL the lobby routes to (single `/?g=CAR1` vs multi
      // `/carracehub?g=CARM`; see urlParams.resolveGameMode) instead of
      // prompting the player at launch. Original dialog code kept below
      // (commented) for reference.
      //
      // // Mode selection — mirrors game_screen.dart BlocListener on
      // // GamePhase.modeSelection. Player picks single or multi player.
      // this.d.state.setPhase('modeSelection');
      // this.d.hud.setStatus('Select mode');
      // const mode: GameMode = await showModeSelectionDialog();
      // debugLog('[Orchestrator] mode selected:', mode);
      const mode: GameMode = this.d.params.gameMode;
      debugLog('[Orchestrator] mode from launch URL (g/path):', mode);

      if (mode === 'multi') {
        await this.startMultiplayer();
      } else {
        this.startBettingRound();
      }
    } catch (e) {
      error('[Orchestrator] start failed:', e);
      this.d.state.setError(extractMessage(e));
      this.d.hud.setStatus('ERROR: ' + extractMessage(e));
    }
  }

  // =================================================================
  // Bootstrap
  // =================================================================

  private async signIn(): Promise<void> {
    const { params } = this.d;
    const agentId = params.agentId || DEFAULT_AGENT_ID;

    let bearerToken: string;
    let gsid: string;

    if (params.token && params.gsid) {
      debugLog('[Orchestrator] Using token+gsid from URL params (no login)');
      bearerToken = ensureUrlEncoded(params.token);
      gsid = params.gsid;
    } else {
      this.d.hud.setStatus('Logging in…');
      debugLog('[Orchestrator] login', params.userName, agentId);
      const login = await this.d.api.login({
        userName: params.userName,
        agentId,
        password: DEFAULT_PASSWORD,
      });
      if (!login.sessionId) throw new Error('Login response missing SessionId');

      this.d.api.setCredentials({
        sessionToken: login.sessionId,
        gameSessionId: null,
        agentId: login.agentId || agentId,
        userName: params.userName,
      });

      this.d.hud.setStatus('Opening game…');
      const session = await this.d.api.openGame({
        userName: params.userName,
        agentId: login.agentId || agentId,
        gameKey: GAME_KEY_SINGLE,
      });
      debugLog('[Orchestrator] openGame OK, currentGameUrl=', session.currentGameUrl);

      const parsed = parseGameUrl(session.currentGameUrl, login.sessionId);
      bearerToken = ensureUrlEncoded(parsed.gameToken);
      gsid = parsed.gsid || session.sessionId;

      this.d.state.setBalance(session.balance);

      this.applyServerConfig(
        session.chips,
        session.minBet,
        session.maxBet,
        session.gameOdds,
      );
    }

    this.d.api.setCredentials({
      sessionToken: bearerToken,
      gameSessionId: gsid,
      agentId,
      userName: params.userName,
    });
    this.gid = gsid;

    this.d.hud.setStatus('Fetching balance…');
    try {
      const balance = await this.d.api.checkBalance({ sessionId: gsid });
      this.d.state.setBalance(balance);
    } catch (e) {
      warn('[Orchestrator] checkBalance failed (continuing):', e);
    }
  }

  private async loadConfigAndBalance(): Promise<void> {
    const sessionId = this.d.api.getCredentials().gameSessionId ?? '';
    try {
      const info = await this.d.api.getInfo({ sessionId });
      if (info.balance > 0) this.d.state.setBalance(info.balance);
      this.applyServerConfig(info.chips, info.minBet, info.maxBet, info.gameOdds);
    } catch (e) {
      warn('[Orchestrator] getInfo failed (continuing with defaults):', e);
      if (this.d.state.config.chipValues.length === 0) {
        this.d.state.setConfig({
          minBet: DEFAULT_GAME_CONFIG.minBet,
          maxBet: DEFAULT_GAME_CONFIG.maxBet,
          chipValues: [...DEFAULT_GAME_CONFIG.chipValues],
          gameOdds: [...DEFAULT_GAME_CONFIG.gameOdds],
        });
      }
    }
  }

  private applyServerConfig(
    chips: number[],
    minBet: number,
    maxBet: number,
    gameOdds: number[],
  ): void {
    const current = this.d.state.config;
    const nextChips = chips.length > 0 ? chips : [...current.chipValues];
    const nextMin = minBet > 0 ? minBet : current.minBet;
    const nextMax = maxBet > 0 ? maxBet : current.maxBet;
    const nextOdds = gameOdds.length > 0 ? gameOdds : [...current.gameOdds];
    debugLog(
      '[Orchestrator] applyServerConfig chips=',
      nextChips,
      'min=',
      nextMin,
      'max=',
      nextMax,
      'odds=',
      nextOdds,
    );
    this.d.state.setConfig({
      minBet: nextMin,
      maxBet: nextMax,
      chipValues: nextChips,
      gameOdds: nextOdds,
    });
    // Render odds now if Rive is already live; also re-applied at each betting
    // round start (see startBettingRound) in case Rive wasn't ready yet.
    this.renderOdds();
  }

  /** Push the current config's payout odds into the Rive odds labels. */
  private renderOdds(): void {
    const odds = this.d.state.config.gameOdds;
    if (odds.length > 0) this.d.rive.setAllOdds(odds);
  }

  // =================================================================
  // Rive event bindings
  // =================================================================

  private bindRiveEvents(): void {
    // Bet-area click. The clickable bet boxes carry a 1-based `betID` (1..8)
    // property identifying the area. Also opportunistically picks up a
    // `chipsAmt` property that, when non-zero, updates the selected chip.
    //
    // The re-exported carracer.riv fires bet clicks as `MouseClick` (the old
    // `BetButton` event was removed). Bind the handler to BOTH so the
    // orchestrator works against either .riv contract; the `betID` guard means
    // a `MouseClick` without a valid 1..8 betID (any non-bet click) is ignored.
    // `MouseClick` also drives the click SFX (audio/soundWiring) — a single
    // event can have multiple listeners.
    const handleBetClick = (payload: Record<string, unknown>): void => {
      this.maybeSelectChipFromAmount(payload);
      const id = asNumber(payload.betID ?? payload.betId ?? payload.id);
      if (!id) return;
      const bet = betTypeFromRiveBetId(id);
      if (bet) this.tryPlaceBet(bet);
    };
    this.d.rive.on('BetButton', handleBetClick);
    this.d.rive.on('MouseClick', handleBetClick);

    this.d.rive.on('evtClearClick', (payload) => {
      this.maybeSelectChipFromAmount(payload);
      this.clearAll();
    });

    this.d.rive.on('evtButtonPlayClick', (payload) => {
      this.maybeSelectChipFromAmount(payload);
      void this.confirm();
    });

    this.d.rive.on('evtSoundOnOff', () => {
      // Toggling lives entirely inside the Rive; we just acknowledge.
      debugLog('[Orchestrator] evtSoundOnOff');
    });

    this.d.rive.on('evtMusicOnOff', () => {
      debugLog('[Orchestrator] evtMusicOnOff');
    });

    // Race animation finished → run ending sequence + schedule finalize.
    this.d.rive.on('spinEnd', () => this.onSpinEnd());

    this.d.rive.on('allControllersReady', () => {
      debugLog('[Orchestrator] Rive controllers ready');
    });
  }

  /**
   * Rive emits a `chipsAmt` property on most input events. When > 0 it's
   * the chip VALUE (not index) the user is currently using. Mirror back
   * to the chip picker by mapping value → index.
   */
  private maybeSelectChipFromAmount(payload: Record<string, unknown>): void {
    const amt = asNumber(payload.chipsAmt);
    if (!amt) return;
    const chips = this.d.state.config.chipValues;
    const idx = chips.indexOf(amt);
    if (idx >= 0) this.d.state.selectChip(idx);
  }

  // =================================================================
  // Player actions
  // =================================================================

  /** Place a bet from a bet-area click. Validates against balance/max. */
  tryPlaceBet(bet: BetType): void {
    const s = this.d.state;
    if (s.phase !== 'betting') return;
    if (s.isMultiplayer && !s.multiplayerCanBet) return;

    const chip = s.selectedChipValue;
    const ok = s.placeBet(bet, chip);
    if (!ok) {
      debugLog('[Orchestrator] placeBet rejected', bet, chip);
      return;
    }
    // The available balance drops at once via onStateChange (req: balance
    // reflects the bet immediately). Bets are NOT sent to the backend here —
    // single-player submits on Play (confirm) and multiplayer submits when
    // the bet countdown ends — so they stay locally clearable until then
    // (req: player can still clear the bets).
  }

  /** Wipe all bets locally. */
  clearAll(): void {
    const s = this.d.state;
    if (s.phase !== 'betting') return;
    // In multiplayer, once the countdown ends we auto-submit and lock the
    // bets — disallow clearing past that point.
    if (s.isMultiplayer && !s.multiplayerCanBet) return;
    s.clearBets();
  }

  /** Single-player only: submit bets, then drive the race / ending flow. */
  async confirm(): Promise<void> {
    if (this.d.state.phase !== 'betting') return;
    if (this.d.state.isMultiplayer) {
      debugLog('[Orchestrator] confirm() ignored in multiplayer mode');
      return;
    }

    const bets = this.d.state.bets.toBetLines();
    if (bets.length === 0) return;

    this.d.state.setPhase('bettingClosed');
    this.d.state.setPhase('waitingResult');

    try {
      const sid = this.d.api.getCredentials().gameSessionId ?? '';
      const result = await this.d.api.placeBet({
        sessionId: sid,
        bets,
        gid: this.gid || sid,
      });

      const parsed = parseResultString(result.result);
      this.gid = result.nextRoundId || this.gid;

      const totalBet = this.d.state.bets.total;
      const betDetails = formatBetDetails(this.d.state.bets);
      const balanceBefore = this.d.state.balance;

      const round: GameRound = {
        roundId: this.gid,
        nextRoundId: result.nextRoundId,
        winner: parsed.winner,
        winningBetItemId: parsed.winningBetItemId,
        totalWinLose: result.totalWinLose,
        totalBet,
        positiveWinAmount: result.positiveWinAmount,
        hasPositiveWin: result.hasPositiveWin,
      };
      this.d.state.applyRoundResult(round);
      // Defer the balance update to the win/lose reveal (+1500 ms after
      // spinEnd) so the displayed balance stays "bet deducted" through the
      // race and only reflects winnings when the result is shown (req 2).
      this.pendingSettleBalance = result.afterBalance;

      GameLogManager.add({
        betAmount: totalBet,
        betDetails,
        winLoseAmount: result.totalWinLose,
        balanceBefore,
        balanceAfter: result.afterBalance,
        gameResult: `${labelOf(parsed.winner)} won`,
        roundId: this.gid,
      });

      this.d.state.setPhase('racing');
      // "No more bets" popup on the MainWinLose overlay.
      this.d.rive.fireNoMoreBet();
      this.d.hud.setStatus('No more bets');

      void this.playRaceAnimation(
        parsed.winningBetItemId,
        SPIN_INITIAL_DELAY_SINGLE_MS,
      );

      // Safety net — if spinEnd never fires, finalize after 10 s.
      this.scheduleTimer(() => {
        if (!this.didFinalizeRound) this.finalizeRound();
      }, FINALIZE_AFTER_BET_SINGLE_MS);
    } catch (e) {
      error('[Orchestrator] placeBet failed:', e);
      const msg = extractMessage(e);
      if (e instanceof ApiError && e.unauthorized) {
        this.d.state.setError('Session expired. Please reload.');
      } else {
        this.d.state.setError(msg);
      }
      this.d.hud.setStatus('ERROR: ' + msg);
    }
  }

  /**
   * Submit the locally-accumulated bets to the multiplayer hub. Invoked when
   * the bet countdown ends (and, as a fallback, on the server's EndBet) —
   * the multiplayer analog of single-player confirm().
   *
   * One PlaceBet is sent per INDIVIDUAL chip (via toChipLines), NOT one per
   * bet-type carrying the accumulated total. The hub validates each submitted
   * amount against the configured chip denominations and rejects a summed
   * total (e.g. 300) with "Invalid Chip Amount"; submitting each chip keeps
   * every amount a valid denomination. The server adds each amount to the
   * player's stake, so the per-chip lines sum to the same total. Idempotent:
   * the didSubmitMpBets guard prevents a double-submit when both the countdown
   * tick and EndBet fire.
   */
  private submitMultiplayerBets(): void {
    const s = this.d.state;
    if (!s.isMultiplayer) return;
    if (this.didSubmitMpBets) return;
    this.didSubmitMpBets = true;
    // Lock betting locally so no further chips/clears slip in after submit.
    s.setMultiplayerCanBet(false);

    const lines = s.bets.toChipLines();
    debugLog(
      '[Orchestrator] submitMultiplayerBets — submitting',
      lines.length,
      'chip line(s):',
      lines,
    );
    if (lines.length === 0) return;
    for (const line of lines) {
      void this.d.hub.placeBet(line.betItemId, line.amount);
    }
  }

  // =================================================================
  // Phase / animation orchestration
  // =================================================================

  /**
   * Begin a new betting round (single-player only).
   * Mirrors `_startSingleBettingRound` in game_screen.dart — clears guards,
   * resets bets/chips, sets bet limits, renders history.
   */
  private startBettingRound(): void {
    this.cancelTimers();
    this.roundToken++;
    this.didStartRace = false;
    this.didFinalizeRound = false;
    this.didRecordHistory = false;
    this.pendingSettleBalance = null;
    this.didSettle = false;
    this.awaitingSettleBalance = false;
    this.didApplyResultSettlement = false;
    this.didSubmitMpBets = false;
    this.didShowWinLose = false;
    this.endingRevealDone = false;
    this.resetRoundLogSnapshot();

    this.d.state.resetForNewRound();
    this.d.rive.clearWinning();
    this.d.rive.clearAllSubTotals();
    // Reset the all-players total row to 0 (req: txtAllTotal reads 0 at round
    // start instead of the .riv's default placeholder text).
    this.d.rive.clearAllAllTotals();
    this.d.state.setPhase('betting');

    const cfg = this.d.state.config;
    this.d.rive.setBetLimits(`${cfg.minBet}`, `${cfg.maxBet}`);
    this.d.rive.setBalanceText(formatCurrency(this.d.state.availableBalance));
    this.d.rive.setTotalBet('0');
    this.renderOdds();
    this.d.rive.setAllHistory(this.d.state.history);

    // "Place your bets" popup on the MainWinLose overlay.
    this.d.rive.fireStartBet();

    this.d.hud.setStatus('Place your bets');
  }

  /**
   * Race animation. Mirrors `showResult` in
   *   packages/amgcarracing_v2/lib/presentation/rive/rive_controller_manager.dart:248-263
   * - wait `initialDelay`
   * - pick a random spin position from SPIN_MAPPING[betItemId]
   * - set Spin = 0 (reset), wait 100 ms, set Spin = chosen value (1..32)
   */
  private async playRaceAnimation(
    winningBetItemId: number,
    initialDelayMs: number,
  ): Promise<void> {
    if (this.didStartRace) return;
    this.didStartRace = true;

    const candidates = SPIN_MAPPING[winningBetItemId];
    if (!candidates || candidates.length === 0) {
      warn('[Orchestrator] No SPIN_MAPPING for betItemId', winningBetItemId);
      return;
    }
    const spinValue = candidates[Math.floor(Math.random() * candidates.length)];

    if (initialDelayMs > 0) await wait(initialDelayMs);
    this.d.rive.setSpin(0);
    await wait(SPIN_RESET_GAP_MS);
    this.d.rive.setSpin(spinValue);
  }

  /**
   * Rive emitted `spinEnd` — the race finished. Schedule the ending
   * animation steps:
   *   t=+1500 ms : settle balance, setWinning(378 - winnerId),
   *                setResultAmount(player win)
   *   t=+5000 ms (relative) : clearWinning
   *   t=+6500 ms : finalize the round
   */
  private onSpinEnd(): void {
    if (this.didFinalizeRound) return;
    this.cancelTimers();

    const round = this.d.state.currentRound;
    if (!round) {
      // Safety: still schedule finalize so we don't get stuck.
      this.scheduleTimer(() => this.finalizeRound(), FINALIZE_AFTER_SPIN_END_MS);
      return;
    }

    this.scheduleTimer(() => {
      // Reflect the post-round balance now that the result is shown (req 2).
      // Clears the resolved bets in the same commit so the available balance
      // lands exactly on the server's settled value.
      this.applySettlement();
      // Race is over and the winner is shown — append to the history road here
      // (not at race start) so the road never spoils the winner early.
      this.recordHistoryOnce(round.winningBetItemId);
      const lane = 378 - round.winningBetItemId;
      this.d.rive.setWinning(lane);
      // The amount to DISPLAY: the gross winnings when the player won ANY bet
      // area (even if the net is a loss → still "YOU WIN"), else the net loss,
      // else 0 (no bet / push). Read FRESH from currentRound — in multiplayer
      // the settlement Result patches the real figures in via applyRoundResult
      // (which REPLACES currentRound) and can land in the 1.5 s gap before this
      // callback; the captured `round` would still hold the StartPlay
      // placeholders. Falls back to the captured round if currentRound was
      // already nulled by an early finalizeRound. (winningBetItemId / winner are
      // immutable across the Result patch, so the captured `round` is fine for
      // those.) Mirrors the Flutter game's displayWinLose.
      const displayWinLose = this.displayWinLoseFor(this.d.state.currentRound ?? round);
      if (displayWinLose !== 0) {
        const sign = displayWinLose > 0 ? '+' : '-';
        this.d.rive.setResultAmount(`${sign}${formatCurrency(Math.abs(displayWinLose))}`);
        this.d.hud.setStatus(
          displayWinLose > 0
            ? `YOU WIN ${formatCurrency(displayWinLose)}`
            : `YOU LOSE ${formatCurrency(-displayWinLose)}`,
        );
      } else {
        this.d.rive.setResultAmount('');
        this.d.hud.setStatus(`${labelOf(round.winner)} won`);
      }
      // YOU WIN / YOU LOSE popup on the MainWinLose overlay. The reveal point
      // is reached now — flag it so a late multiplayer Result can drive the
      // popup as a backstop (see handleResult / revealWinLosePanel). In single
      // player (and multiplayer when the Result already arrived) currentRound
      // holds the real figure, so this shows immediately; otherwise display is
      // 0 here and the backstop reveals it once the settlement lands.
      this.endingRevealDone = true;
      this.revealWinLosePanel(displayWinLose);
      this.d.state.setPhase('showResult');
    }, ENDING_ANIMATION_DELAY_MS);

    this.scheduleTimer(() => {
      this.d.rive.clearWinning();
      this.d.rive.setResultAmount('');
    }, ENDING_ANIMATION_DELAY_MS + WINNING_HIDE_DELAY_MS);

    this.scheduleTimer(() => this.finalizeRound(), FINALIZE_AFTER_SPIN_END_MS);
  }

  /**
   * Append this round's winner to the history road exactly once. Primary call
   * is in onSpinEnd (race finished, winner shown). finalizeRound calls it again
   * as a safety net BEFORE resetForNewRound() nulls currentRound, so a round can
   * never be dropped from the road even if spinEnd never fired.
   */
  private recordHistoryOnce(winningBetItemId: number): void {
    if (this.didRecordHistory) return;
    this.didRecordHistory = true;
    this.d.state.recordRoundHistory(winningBetItemId);
  }

  /**
   * The win/lose figure to DISPLAY for a round — mirrors the Flutter game's
   * displayWinLose: the GROSS winnings when the player won any bet area (even
   * if the net is a loss, so it still reads "YOU WIN"), otherwise the net loss,
   * otherwise 0 (no bet / push). Positive = win, negative = loss, 0 = neither.
   */
  private displayWinLoseFor(round: GameRound | null): number {
    if (!round) return 0;
    if (round.positiveWinAmount > 0) return round.positiveWinAmount;
    return round.totalWinLose < 0 ? round.totalWinLose : 0;
  }

  /**
   * Show the YOU WIN / YOU LOSE popup on the MainWinLose overlay exactly once
   * per round. `display` follows displayWinLoseFor: positive = win (show the
   * gross winnings — fires whenever the player won ANY bet area, even on a net
   * loss), negative = lose (the net loss), 0 = nothing (push / no bet → no
   * popup). Idempotent via didShowWinLose; a 0 leaves the guard unset so a later
   * caller with the real figure can still reveal it.
   *
   * The caller passes `display` EXPLICITLY rather than this reading currentRound
   * itself, because in multiplayer the authoritative settlement Result lands
   * ~6.5 s+ after spinEnd — after finalizeRound() has nulled currentRound — so
   * the popup must be driven from the amount extracted in handleResult, not from
   * (the by-then absent) currentRound.
   */
  private revealWinLosePanel(display: number): void {
    if (this.didShowWinLose) return;
    if (display === 0) return;
    this.didShowWinLose = true;
    this.d.rive.showWinLose(display > 0, formatCurrency(Math.abs(display)));
  }

  /** Clear the per-round Game-Log snapshot (see field docs). */
  private resetRoundLogSnapshot(): void {
    this.pendingLogRound = null;
    this.pendingLogBetTotal = 0;
    this.pendingLogBetDetails = '';
    this.didLogRound = false;
  }

  /**
   * Apply the deferred balance settlement: set the wallet to the server's
   * post-round value and clear the resolved bets (atomically, to avoid a
   * one-frame flash of an intermediate available balance). Idempotent — safe
   * to call from the reveal timer and again from finalizeRound's safety net.
   * If the server balance isn't known yet (a late multiplayer Result), the
   * bets are left in place; handleResult finalizes the moment it arrives.
   */
  private applySettlement(): void {
    if (this.didSettle) return;
    if (this.pendingSettleBalance != null) {
      // Server's post-round balance is known — finalize atomically.
      this.didSettle = true;
      this.d.state.settle(this.pendingSettleBalance);
    } else {
      // Multiplayer: the reveal reached the settle point but the server's
      // Result event (carrying the post-round balance) hasn't arrived yet.
      // Do NOT clear the bets here — the displayed balance is
      // `wallet − pending bets`, so clearing the bets while the wallet still
      // holds the pre-settlement value would make the balance jump UP to the
      // full (pre-bet) wallet for a moment (the "balance flashes to the old
      // value" bug). Instead, leave the bets so the display stays
      // bet-deducted, and let handleResult finalize atomically the instant
      // the balance arrives.
      this.awaitingSettleBalance = true;
    }
  }

  /**
   * End-of-round teardown. Clears local bets/highlight/chips/text.
   *  - Single player → immediately restarts the betting round.
   *  - Multiplayer   → waits for the server's StartGame / StartBet events.
   */
  private finalizeRound(): void {
    if (this.didFinalizeRound) return;
    this.didFinalizeRound = true;
    this.cancelTimers();

    // Safety net: ensure the balance was settled even if the reveal timer
    // was skipped (e.g. spinEnd never fired and a fallback finalized) —
    // BEFORE resetForNewRound clears the bets below.
    this.applySettlement();

    // Safety net: ensure this round made it onto the history road even if the
    // onSpinEnd reveal never ran — BEFORE resetForNewRound() nulls currentRound.
    const finished = this.d.state.currentRound;
    if (finished) this.recordHistoryOnce(finished.winningBetItemId);

    this.d.rive.clearWinning();
    this.d.rive.setResultAmount('');
    this.d.rive.clearAllSubTotals();
    this.d.rive.setTotalBet('0');

    if (this.d.state.isMultiplayer) {
      this.d.rive.clearAllAllTotals();
      this.d.state.setMultiplayerTotalBets(null);
      this.d.state.resetForNewRound();
      this.d.state.setPhase('newRound');
      this.d.rive.setAllHistory(this.d.state.history);
      this.d.hud.setStatus('Waiting for next round…');
    } else {
      this.startBettingRound();
    }
  }

  // =================================================================
  // State-change → output sync
  // =================================================================

  private onStateChange(next: GameStateSnapshot, prev: GameStateSnapshot): void {
    if (next.balance !== prev.balance) {
      this.renderBalance();
    }

    if (next.bets !== prev.bets) {
      this.d.hud.setBetTotal(next.bets.total);
      this.d.rive.setTotalBet(formatCurrency(next.bets.total));
      // Placing or clearing a bet changes the available balance — reflect
      // it immediately (req: balance updates the instant a bet is placed).
      this.renderBalance();
      this.d.controls.setControlsEnabled(this.canInteract(next), !next.bets.isEmpty);
      // Per-area subtotals (txtSubTotal1..8) = the player's OWN bet (both modes,
      // mirrors Flutter _updateSingleBetTexts / _updateMultiplayerBetTexts).
      ALL_BETS.forEach((bet, i) => {
        const amt = next.bets.amountFor(bet);
        this.d.rive.setSubTotal(i + 1, amt > 0 ? formatCurrency(amt) : '');
        // Single-player has no other players, so the all-players total
        // (txtAllTotal) equals your own bet — mirror it here so it isn't stuck
        // at 0 (req). In multiplayer, txtAllTotal is driven by the backend
        // TotalBets map (multiplayerTotalBets handler below), not touched here.
        if (!next.isMultiplayer) {
          this.d.rive.setAllTotal(i + 1, amt > 0 ? formatCurrency(amt) : '0');
        }
      });
    }

    if (next.selectedChipIndex !== prev.selectedChipIndex) {
      this.d.controls.setSelectedChip(next.selectedChipIndex);
    }

    if (
      next.phase !== prev.phase ||
      next.multiplayerCanBet !== prev.multiplayerCanBet
    ) {
      this.d.controls.setControlsEnabled(this.canInteract(next), !next.bets.isEmpty);
    }

    if (next.config !== prev.config) {
      this.d.controls.bindChips(next.config);
      this.d.controls.setSelectedChip(next.selectedChipIndex);
    }

    if (next.history !== prev.history) {
      this.d.rive.setAllHistory(next.history);
    }

    if (next.multiplayerTotalBets !== prev.multiplayerTotalBets) {
      const map = next.multiplayerTotalBets ?? {};
      ALL_BETS.forEach((bet, i) => {
        const v = map[bet];
        // Areas with no all-players bets read "0" (not blank) to match the
        // round-start zeroing rather than reverting to the .riv default text.
        this.d.rive.setAllTotal(i + 1, v != null && v > 0 ? formatCurrency(v) : '0');
      });
    }

    if (
      next.multiplayerBetSeconds !== prev.multiplayerBetSeconds ||
      next.multiplayerRestartSeconds !== prev.multiplayerRestartSeconds
    ) {
      if (next.multiplayerBetSeconds != null) {
        this.d.timer.showBetTimer(next.multiplayerBetSeconds);
      } else if (next.multiplayerRestartSeconds != null) {
        this.d.timer.showRestartTimer(next.multiplayerRestartSeconds);
      } else {
        this.d.timer.showBetTimer(null);
      }
    }
  }

  /** Push the available balance (wallet − pending bets) to HUD + Rive. */
  private renderBalance(): void {
    const avail = this.d.state.availableBalance;
    this.d.hud.setBalance(avail);
    this.d.rive.setBalanceText(formatCurrency(avail));
  }

  /**
   * Whether bet / play / clear controls should be live. Single-player: the
   * betting phase. Multiplayer: the hub's StartBet/EndBet and our
   * countdown-end submit gate it via `multiplayerCanBet`.
   */
  private canInteract(s: GameStateSnapshot): boolean {
    return s.isMultiplayer ? s.multiplayerCanBet : s.phase === 'betting';
  }

  // =================================================================
  // Multiplayer
  // =================================================================

  private async startMultiplayer(): Promise<void> {
    this.d.state.setMultiplayerActive(true);
    this.d.state.setPhase('initializing');
    this.d.hud.setStatus('Connecting hub…');

    try {
      await this.d.hub.connect();

      // JoinGame sessionId is the URL-DECODED bearer token, NOT the gsid.
      const encodedToken = this.d.api.getCredentials().sessionToken ?? '';
      const sessionId = encodedToken.includes('%')
        ? decodeURIComponent(encodedToken)
        : encodedToken;
      if (!sessionId) throw new Error('Missing session token for JoinGame');

      debugLog(
        '[Orchestrator] joining hub with sessionId (decoded token), gameKey=',
        GAME_KEY_MULTI,
      );
      await this.d.hub.joinGame(sessionId, GAME_KEY_MULTI);
      await this.d.hub.getMessages();
      debugLog('[Orchestrator] joined multiplayer table');
      this.d.hud.setStatus('Waiting for next round…');
    } catch (e) {
      error('[Orchestrator] multiplayer connect failed:', e);
      this.d.state.setError(extractMessage(e));
      this.d.state.setMultiplayerActive(false);
    }
  }

  private bindHubEvents(): void {
    this.d.hub.on((event) => this.handleHubEvent(event));
  }

  private handleHubEvent(event: HubEvent): void {
    const env = this.normalizeEnvelope(event.payload);

    // Index-based dedup: the server retransmits some events, so skip any
    // envelope whose Index is <= the last one we processed.
    if (env && typeof env.Index === 'number') {
      const idx = env.Index;
      if (this.lastMpEventIndex != null && idx <= this.lastMpEventIndex) return;
      this.lastMpEventIndex = idx;
    }

    // The server RESTARTS its per-message `Index` at 1 every round — the next
    // round's StartGame arrives with Index=1, StartBet with Index=2, … . The
    // inter-round restart event must therefore clear the dedup baseline, or the
    // whole next round is dropped by the check above and the player is left with
    // betting + chip controls permanently disabled. The restart is the Result
    // event carrying State='restart' (canonical — mirrors the carracing Flutter
    // game_bloc.dart `_lastMultiplayerIndex = null` and the other pixi ports) or
    // a restart countdown (StartTime/EndTime), the same signal handleResult uses.
    const isRestart =
      String(env?.State ?? '').toLowerCase() === 'restart' ||
      (event.name === 'Result' &&
        (env?.StartTime != null || env?.EndTime != null));
    if (env && isRestart) this.lastMpEventIndex = null;

    switch (event.name) {
      case '_connection':
        debugLog('[Hub] connection state', event.payload);
        break;
      case 'PlayerJoined':
        this.handlePlayerJoined(env);
        break;
      case 'PlayerLeft':
        this.handlePlayerLeft(env);
        break;
      case 'StartGame':
        this.handleStartGame(env);
        break;
      case 'StartBet':
        this.handleStartBet(env);
        break;
      case 'EndBet':
        this.handleEndBet(env);
        break;
      case 'StartPlay':
        this.handleStartPlay(env);
        break;
      case 'Result':
        this.handleResult(env);
        break;
      case 'PlaceBet':
        this.handlePlaceBetAck(env);
        break;
      case 'GetMessages':
        break;
      default:
        debugLog('[Hub] unhandled event', event.name, env);
    }
  }

  private handlePlayerJoined(env: Envelope): void {
    if (env?._status === 'Fail') {
      const msg = String(env.Content ?? 'Failed to join multiplayer table');
      error('[Hub] PlayerJoined Fail:', msg);
      this.d.state.setError(msg);
      this.d.hud.setStatus('ERROR: ' + msg);
      this.stopMultiplayer();
      this.d.state.setPhase('modeSelection');
      return;
    }

    const game = (env?.Data && typeof env.Data === 'object'
      ? (env.Data as { Game?: unknown }).Game
      : undefined) as Record<string, unknown> | undefined;
    if (game) {
      const chips = parseChipsField(game.Chips ?? game.chips);
      if (chips.length > 0) {
        this.d.state.setConfig({
          ...this.d.state.config,
          chipValues: chips,
        });
      }
      const min = asNumber(game.MinBet ?? game.minBet);
      const max = asNumber(game.MaxBet ?? game.maxBet);
      if (min > 0 || max > 0) {
        this.d.state.setConfig({
          ...this.d.state.config,
          minBet: min > 0 ? min : this.d.state.config.minBet,
          maxBet: max > 0 ? max : this.d.state.config.maxBet,
        });
      }
    }
  }

  private handlePlayerLeft(env: Envelope): void {
    const msg = String(env?.Content ?? 'You have been disconnected.');
    debugLog('[Hub] PlayerLeft kick:', msg);
    this.d.state.setPlayerKickedMessage(msg);
    this.stopMultiplayer();
    this.d.state.setPhase('modeSelection');
    this.d.hud.setStatus('Session ended — please reload');
  }

  /**
   * StartGame — round reset. Mirrors game_bloc.dart + game_screen.dart:951-952:
   * also fires the DiceBowl pre-race animation (carRacing-specific).
   */
  private handleStartGame(_env: Envelope): void {
    this.cancelBetCountdown();
    this.cancelRestartCountdown();
    // Cancel any pending ending/finalize timers from the PREVIOUS round. Without
    // this, a stale round-N finalizeRound() can fire after round N+1 has already
    // re-opened betting — it would resetForNewRound() (wiping the new bets) and
    // setPhase('newRound'), leaving the player unable to bet or click. Mirrors
    // dragontiger's handleStartGame, which clears these here.
    this.cancelTimers();
    this.roundToken++;
    this.didStartRace = false;
    this.didFinalizeRound = false;
    this.didRecordHistory = false;
    this.pendingSettleBalance = null;
    this.didSettle = false;
    this.awaitingSettleBalance = false;
    this.didApplyResultSettlement = false;
    this.didSubmitMpBets = false;
    this.didShowWinLose = false;
    this.endingRevealDone = false;
    this.resetRoundLogSnapshot();

    this.d.state.setMultiplayerCanBet(false);
    this.d.state.setMultiplayerBetSeconds(null);
    this.d.state.setMultiplayerRestartSeconds(null);
    this.d.state.setMultiplayerTotalBets(null);
    this.d.state.resetForNewRound();
    this.d.rive.clearWinning();
    this.d.rive.clearAllSubTotals();
    this.d.rive.clearAllAllTotals();
    this.d.rive.setTotalBet('0');
    this.d.state.setPhase('newRound');
    this.d.rive.setAllHistory(this.d.state.history);
    this.d.rive.fireDiceBowl();
  }

  private handleStartBet(env: Envelope): void {
    const seconds = secondsBetween(env?.StartTime, env?.EndTime);
    debugLog('[Hub] StartBet — countdown', seconds, 's');
    // Fresh bet window — allow a new batched submission this round.
    this.didSubmitMpBets = false;
    this.d.state.setPhase('betting');
    this.d.state.setMultiplayerCanBet(true);
    // "Place your bets" popup on the MainWinLose overlay.
    this.d.rive.fireStartBet();
    this.d.hud.setStatus('Place your bets');
    // End the visible countdown BET_SUBMIT_BUFFER_SEC seconds early so the
    // batched submit (at countdown 0) reaches the backend before the real
    // window closes and the race starts.
    this.startBetCountdown(Math.max(0, seconds - BET_SUBMIT_BUFFER_SEC));
  }

  private handleEndBet(_env: Envelope): void {
    this.cancelBetCountdown();
    // Submit now if the local countdown didn't already (server closed the
    // window first). Idempotent via didSubmitMpBets.
    this.submitMultiplayerBets();
    this.d.state.setMultiplayerCanBet(false);
    this.d.state.setMultiplayerBetSeconds(null);
    this.d.state.setPhase('bettingClosed');
    // "No more bets" popup on the MainWinLose overlay.
    this.d.rive.fireNoMoreBet();
    this.d.hud.setStatus('No more bets');
  }

  /**
   * StartPlay — server has decided the round result; race animation starts
   * immediately (no initial delay in multiplayer). Mirrors
   * game_screen.dart:122-151.
   */
  private handleStartPlay(env: Envelope): void {
    const raw = String(env?.Result ?? '');
    if (!raw) return;
    let parsed;
    try {
      parsed = parseResultString(raw);
    } catch (e) {
      warn('[Hub] StartPlay parse failed:', e, raw);
      return;
    }
    this.d.state.setMultiplayerResultString(parsed.canonical);

    const totalBet = this.d.state.bets.total;
    const round: GameRound = {
      roundId: '',
      nextRoundId: '',
      winner: parsed.winner,
      winningBetItemId: parsed.winningBetItemId,
      // WinLose isn't known yet — comes via the Result event.
      totalWinLose: 0,
      totalBet,
      positiveWinAmount: 0,
      hasPositiveWin: false,
    };
    this.d.state.applyRoundResult(round);

    // Snapshot the round + wagered total/details NOW (bets are still locked in
    // for this round). The settlement Result can arrive after finalizeRound has
    // already cleared currentRound and the bets, so the Game-Log entry is built
    // from this snapshot in handleResult rather than from live state.
    this.pendingLogRound = round;
    this.pendingLogBetTotal = totalBet;
    this.pendingLogBetDetails = formatBetDetails(this.d.state.bets);

    this.d.state.setPhase('racing');
    void this.playRaceAnimation(parsed.winningBetItemId, 0);

    // Safety net — finalize even if spinEnd never fires.
    this.scheduleTimer(() => {
      if (!this.didFinalizeRound) this.finalizeRound();
    }, FINALIZE_AFTER_START_PLAY_MS);
  }

  private handleResult(env: Envelope): void {
    const state = String(env?.State ?? '').toLowerCase();

    if (state === 'restart' || env?.StartTime || env?.EndTime) {
      const seconds = secondsBetween(env?.StartTime, env?.EndTime);
      if (seconds > 0) {
        debugLog('[Hub] Result/restart — restart countdown', seconds, 's');
        this.d.state.setMultiplayerGameState('restart');
        this.startRestartCountdown(seconds);
        return;
      }
    }

    // Settle off the AUTHORITATIVE post-round balance. The backend stopped
    // returning a usable per-bet figure (WinLost is 0 even on a loss) and the
    // Result event no longer carries a per-user Balance, so the wallet can't be
    // derived locally — we read it from the server (/action/balance). Async; the
    // visual ending sequence is driven independently by the Rive `spinEnd` event
    // (or the finalize safety net), so nothing here waits on the balance.
    void this.settleFromResult(env);
  }

  /**
   * Resolve the player's authoritative post-round balance, then run settlement.
   *
   * Balance source, in order of preference:
   *   1. The Result event's per-user `Balance` field — used when the server
   *      still sends it.
   *   2. /action/balance (checkBalance) — the backend now omits Balance from the
   *      Result event AND returns WinLost=0 even on a loss, so neither the wallet
   *      nor the net P&L can be derived locally; the only correct source is the
   *      server's own balance. NOTE: /action/getinfo does NOT carry a balance (it
   *      returns config only), so we must use /action/balance, the same endpoint
   *      the startup balance fetch uses.
   *
   * Async because of the balance round-trip, so it captures the round token up
   * front and bails if a new round started before the balance arrived — a slow
   * fetch must never settle a finished round into the next round's wallet.
   */
  private async settleFromResult(env: Envelope): Promise<void> {
    const token = this.roundToken;
    const userName = (this.d.api.getCredentials().userName || '').toLowerCase();
    const winLose = extractWinLoseForUser(env?.Data, userName);
    let balance = extractBalanceForUser(env?.Data, userName);

    if (balance == null) {
      // Server didn't include the post-round balance — fetch it from
      // /action/balance.
      balance = await this.fetchLatestBalance();
    }

    if (token !== this.roundToken) {
      debugLog(
        '[Hub] Result settlement dropped — round advanced during balance fetch',
      );
      return;
    }

    this.applyResultSettlement(env, winLose, balance);
  }

  /**
   * Fetch the latest wallet balance from the backend via /action/balance
   * (checkBalance) — the same endpoint the startup balance fetch uses.
   * (/action/getinfo carries config only, no balance.) Returns null on failure
   * so the caller can fall back to the locally-tracked wallet rather than
   * zeroing the balance on a transient network error.
   */
  private async fetchLatestBalance(): Promise<number | null> {
    try {
      const sessionId = this.d.api.getCredentials().gameSessionId ?? '';
      const balance = await this.d.api.checkBalance({ sessionId });
      debugLog('[Hub] /action/balance post-result balance =', balance);
      return balance;
    } catch (e) {
      warn('[Hub] checkBalance after result failed — keeping local balance:', e);
      return null;
    }
  }

  /**
   * Apply the resolved post-round balance: settle the wallet, drive the YOU WIN /
   * YOU LOSE popup, and log the round. Once per round (didApplyResultSettlement) —
   * a retransmitted settlement Result must not recompute against the
   * already-settled wallet or double-log. `balance` is the authoritative
   * post-round wallet (from the Result event or /action/balance); null only when
   * both sources failed, in which case we fall back to `balanceBefore + winLose`.
   */
  private applyResultSettlement(
    env: Envelope,
    winLose: number,
    balance: number | null,
  ): void {
    if (this.didApplyResultSettlement) return;
    this.didApplyResultSettlement = true;

    const userName = (this.d.api.getCredentials().userName || '').toLowerCase();
    // Gross winnings from the user's WINNING bet rows (not the net). Drives the
    // "YOU WIN" popup whenever any area won, even if `winLose` (net) is negative.
    const positiveWin = extractPositiveWinForUser(env?.Data, userName);

    debugLog('[Hub] Result settlement — winLose=', winLose, 'balance=', balance);

    const balanceBefore = this.d.state.balance;
    const balanceAfter = balance ?? balanceBefore + winLose;

    this.d.state.setMultiplayerWinLose(winLose);
    // Defer the balance update to the win/lose reveal (+1500 ms after
    // spinEnd), like single-player (req 2).
    this.pendingSettleBalance = balanceAfter;
    // If the reveal already reached the settle point and was only waiting on
    // this balance, finalize now (atomic balance update + bet clear). This is
    // the multiplayer path where the Result event lands after the local
    // ending sequence — settling atomically here avoids clearing bets early
    // (which would flash the pre-settlement wallet).
    if (this.awaitingSettleBalance && !this.didSettle) {
      this.didSettle = true;
      this.awaitingSettleBalance = false;
      this.d.state.settle(balanceAfter);
    }
    // NOTE: no re-sync branch for an already-settled round (matches dragontiger).
    // The server's Result carries `balance: null` here, so `balanceAfter` is the
    // INCREMENTAL `balanceBefore + winLose`. Re-applying that after settlement
    // (e.g. on a duplicate/late Result) would double-count the win/lose and make
    // the wallet flash a wrong value — the round is already settled, so ignore.

    // Update currentRound's win/lose so a not-yet-run reveal shows the right
    // amount. On the late-Result path currentRound is already null (finalizeRound
    // nulled it) and the reveal has passed, so this simply no-ops.
    const cur = this.d.state.currentRound;
    if (cur) {
      this.d.state.applyRoundResult({
        ...cur,
        totalWinLose: winLose,
        positiveWinAmount: positiveWin,
        hasPositiveWin: positiveWin > 0,
      });
    }

    // Drive the YOU WIN / YOU LOSE popup from the AUTHORITATIVE settlement
    // figures extracted above — NOT from currentRound, which finalizeRound()
    // nulls ~6.5 s after spinEnd. The settlement Result routinely lands later
    // than that (which is exactly why the Game-Log is snapshotted), so by the
    // time we get here currentRound is usually already gone. "YOU WIN" (gross
    // winnings) whenever any bet area won — even on a net loss — else "YOU LOSE"
    // (net loss), else nothing. Reveal once the race is over (the ending reveal
    // ran, or a safety net finalized the round) so it never appears mid-race;
    // if the Result arrives BEFORE the race ends, currentRound is patched above
    // and the onSpinEnd reveal shows it instead. Idempotent via didShowWinLose.
    const resultDisplay = positiveWin > 0 ? positiveWin : winLose < 0 ? winLose : 0;
    if (this.endingRevealDone || this.didFinalizeRound) {
      this.revealWinLosePanel(resultDisplay);
    }

    // Record the Game-Log entry from the StartPlay snapshot (winner + wagered
    // total/details), NOT live state — finalizeRound may already have cleared
    // currentRound AND the bets if the Result landed >6.5 s after spinEnd (the
    // bug that left the log empty). Gated by didLogRound so a duplicate/late
    // Result can't double-log.
    const logRound = this.pendingLogRound;
    if (logRound && !this.didLogRound) {
      this.didLogRound = true;
      GameLogManager.add({
        betAmount: this.pendingLogBetTotal,
        betDetails: this.pendingLogBetDetails,
        winLoseAmount: winLose,
        balanceBefore,
        balanceAfter,
        gameResult: `${labelOf(logRound.winner)} won`,
        roundId: logRound.roundId || null,
      });
    }

    // If spinEnd fires later it'll drive the ending animation; nothing to do here.
  }

  /**
   * PlaceBet ack — server confirmed (part of) our batched bet submission.
   *
   * No-op for balance: bets are submitted as a single batch when the
   * countdown ends and the displayed balance is optimistic (wallet −
   * pending bets). The authoritative wallet is re-synced from the Result
   * event at settlement, so applying the ack's per-bet balance here would
   * double-count the deduction.
   *
   * Still applies the per-area TotalBets map (txtAllTotal1..8). Mirrors
   * game_screen.dart:668-697 _updateAllTotalTexts. Local bets are NOT
   * cleared — they accumulate until next StartGame (matches the 2026-01-22
   * baccarat maxBet fix).
   */
  private handlePlaceBetAck(env: Envelope): void {
    const totals = extractTotalBets(env);
    if (totals) this.d.state.setMultiplayerTotalBets(totals);
  }

  // -----------------------------------------------------------------
  // Multiplayer countdown timers
  // -----------------------------------------------------------------

  private startBetCountdown(seconds: number): void {
    this.cancelBetCountdown();
    let remaining = Math.max(0, Math.floor(seconds));
    this.d.state.setMultiplayerBetSeconds(remaining);
    this.betCountdownHandle = window.setInterval(() => {
      remaining -= 1;
      if (remaining <= 0) {
        this.cancelBetCountdown();
        this.d.state.setMultiplayerBetSeconds(null);
        // Countdown ended — submit the batched bets now (req 3: multiplayer
        // submits when the countdown ends, not per-chip).
        this.submitMultiplayerBets();
      } else {
        this.d.state.setMultiplayerBetSeconds(remaining);
      }
    }, 1000);
  }

  private cancelBetCountdown(): void {
    if (this.betCountdownHandle != null) {
      window.clearInterval(this.betCountdownHandle);
      this.betCountdownHandle = null;
    }
  }

  private startRestartCountdown(seconds: number): void {
    this.cancelRestartCountdown();
    let remaining = Math.max(0, Math.floor(seconds));
    this.d.state.setMultiplayerRestartSeconds(remaining);
    this.restartCountdownHandle = window.setInterval(() => {
      remaining -= 1;
      if (remaining <= 0) {
        this.cancelRestartCountdown();
        this.d.state.setMultiplayerRestartSeconds(null);
      } else {
        this.d.state.setMultiplayerRestartSeconds(remaining);
      }
    }, 1000);
  }

  private cancelRestartCountdown(): void {
    if (this.restartCountdownHandle != null) {
      window.clearInterval(this.restartCountdownHandle);
      this.restartCountdownHandle = null;
    }
  }

  private stopMultiplayer(): void {
    this.cancelBetCountdown();
    this.cancelRestartCountdown();
    void this.d.hub.disconnect();
    this.d.state.setMultiplayerActive(false);
  }

  // =================================================================
  // Helpers
  // =================================================================

  private normalizeEnvelope(payload: unknown): Envelope {
    if (payload == null) return null;
    if (Array.isArray(payload)) {
      for (const item of payload) {
        if (item && typeof item === 'object' && !Array.isArray(item)) {
          const status = typeof payload[0] === 'string' ? (payload[0] as string) : null;
          if (status) {
            return { _status: status, ...(item as Record<string, unknown>) };
          }
          return item as Record<string, unknown>;
        }
      }
      return null;
    }
    if (typeof payload === 'object') return payload as Envelope;
    return null;
  }

  private scheduleTimer(fn: () => void, ms: number): void {
    const id = window.setTimeout(fn, ms);
    this.sequenceTimers.push(id);
  }

  private cancelTimers(): void {
    for (const id of this.sequenceTimers) window.clearTimeout(id);
    this.sequenceTimers = [];
  }
}

// =====================================================================
// Free helpers
// =====================================================================

type Envelope = Record<string, unknown> | null;

function wait(ms: number): Promise<void> {
  return new Promise((r) => setTimeout(r, ms));
}

function formatCurrency(n: number): string {
  return Math.round(n).toLocaleString('en-US');
}

function ensureUrlEncoded(token: string): string {
  if (!token) return token;
  if (token.includes('%')) return token;
  return encodeURIComponent(token);
}

function parseGameUrl(
  currentGameUrl: string,
  loginToken: string,
): { gameToken: string; gsid: string } {
  if (!currentGameUrl) return { gameToken: loginToken, gsid: '' };
  try {
    const u = new URL(currentGameUrl, 'https://placeholder.invalid');
    const urlToken = u.searchParams.get('token');
    const urlGsid = u.searchParams.get('gsid');
    return {
      gameToken: urlToken && urlToken.length > 0 ? urlToken : loginToken,
      gsid: urlGsid ?? '',
    };
  } catch {
    return { gameToken: loginToken, gsid: '' };
  }
}

function formatBetDetails(bets: {
  bets: readonly BetType[];
  amountFor(b: BetType): number;
}): string {
  const parts: string[] = [];
  for (const b of bets.bets) {
    const amt = bets.amountFor(b);
    if (amt > 0) parts.push(`${labelOf(b)}:${formatCurrency(amt)}`);
  }
  return parts.join(', ');
}

function extractMessage(e: unknown): string {
  if (e instanceof Error) return e.message;
  if (typeof e === 'string') return e;
  try {
    return JSON.stringify(e);
  } catch {
    return String(e);
  }
}

function asNumber(v: unknown): number {
  if (typeof v === 'number') return v;
  if (typeof v === 'string') {
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
  }
  return 0;
}

function parseChipsField(raw: unknown): number[] {
  const out: number[] = [];
  if (typeof raw === 'string') {
    for (const piece of raw.split(',')) {
      const n = parseInt(piece.trim(), 10);
      if (Number.isFinite(n) && n > 0) out.push(n);
    }
  } else if (Array.isArray(raw)) {
    for (const v of raw) {
      const n = typeof v === 'number' ? v : parseInt(String(v), 10);
      if (Number.isFinite(n) && n > 0) out.push(n);
    }
  }
  return out;
}

function secondsBetween(start: unknown, end: unknown): number {
  if (typeof start !== 'string' || typeof end !== 'string') return 0;
  const a = Date.parse(start);
  const b = Date.parse(end);
  if (!Number.isFinite(a) || !Number.isFinite(b)) return 0;
  return Math.round((b - a) / 1000);
}

function extractWinLoseForUser(data: unknown, userNameLower: string): number {
  if (data == null || !userNameLower) return 0;

  if (Array.isArray(data)) {
    let sum = 0;
    for (const raw of data) {
      if (!raw || typeof raw !== 'object') continue;
      const item = raw as Record<string, unknown>;
      const u = pickUserName(item);
      // Skip broadcast/empty-username rows AND rows for other players.
      if (!u || u !== userNameLower) continue;
      sum += asNumber(item.WinLost ?? item.WinLose ?? item.winLost ?? item.winLose);
    }
    return sum;
  }
  if (typeof data === 'object') {
    const m = data as Record<string, unknown>;
    // Single-map shape (rare): require a user match if a username is present,
    // otherwise fall back to the top-level total.
    const u = pickUserName(m);
    if (u && u !== userNameLower) return 0;
    return asNumber(m.TotalWinLose ?? m.WinLose ?? m.winLose ?? m.WinLost);
  }
  return 0;
}

/**
 * Sum ONLY the current user's POSITIVE WinLose rows out of the Data array — the
 * gross winnings from the bet areas that WON. Mirrors the Flutter game's
 * positiveForUser (_computeMultiplayerWinLoseSummary). Used so the YOU WIN popup
 * shows whenever the player won any area, even if their net result is a loss.
 * Same row-matching rules as extractWinLoseForUser (user rows only).
 */
function extractPositiveWinForUser(data: unknown, userNameLower: string): number {
  if (data == null || !userNameLower) return 0;

  if (Array.isArray(data)) {
    let sum = 0;
    for (const raw of data) {
      if (!raw || typeof raw !== 'object') continue;
      const item = raw as Record<string, unknown>;
      const u = pickUserName(item);
      if (!u || u !== userNameLower) continue;
      const v = asNumber(item.WinLost ?? item.WinLose ?? item.winLost ?? item.winLose);
      if (v > 0) sum += v;
    }
    return sum;
  }
  if (typeof data === 'object') {
    const m = data as Record<string, unknown>;
    const u = pickUserName(m);
    if (u && u !== userNameLower) return 0;
    const v = asNumber(m.TotalWinLose ?? m.WinLose ?? m.winLose ?? m.WinLost);
    return v > 0 ? v : 0;
  }
  return 0;
}

/**
 * Extract the current user's Balance from a Result / PlaceBet envelope's
 * `Data` field. Ported from amg_dragontiger_pixi (same hub messages).
 *
 * Three rules that MUST be observed (each corresponds to a real balance bug
 * we hit on this server):
 *
 *   1. The primary field name is `Username` (capital-U, lower-n) — the
 *      lowercase / Pascal-case variants only exist on some events. Probe
 *      `Username` → `username` → `UserName` → `userName` (see pickUserName).
 *
 *   2. Entries with empty / missing username MUST be skipped, not matched.
 *      Hub broadcasts include "info-only" entries with no username carrying
 *      placeholder Balance values (often 0 or the dealer's running totals).
 *      Treating those as a match drops the player's balance to 0 mid-round.
 *
 *   3. If the matched entry has `Balance: null`, return null (don't update).
 *      Coercing a null/non-numeric balance to 0 (the old asNumber path) is
 *      what blanked the wallet right after the first bet.
 *
 * Both the array and single-map data shapes apply the same rules.
 */
function extractBalanceForUser(data: unknown, userNameLower: string): number | null {
  if (data == null || !userNameLower) return null;

  if (Array.isArray(data)) {
    for (const raw of data) {
      if (!raw || typeof raw !== 'object') continue;
      const item = raw as Record<string, unknown>;
      const u = pickUserName(item);
      if (!u || u !== userNameLower) continue; // rule 2
      const b = item.Balance ?? item.balance;
      if (b == null) return null; // rule 3
      const n = typeof b === 'number' ? b : Number(b);
      return Number.isFinite(n) ? n : null;
    }
    return null;
  }

  if (typeof data === 'object') {
    const m = data as Record<string, unknown>;
    const u = pickUserName(m);
    if (!u || u !== userNameLower) return null; // rule 2 — never trust a no-username row
    const b = m.Balance ?? m.balance;
    if (b == null) return null; // rule 3
    const n = typeof b === 'number' ? b : Number(b);
    return Number.isFinite(n) ? n : null;
  }

  return null;
}

/** Probe `Username` (canonical) → `username` → `UserName` → `userName`. */
function pickUserName(item: Record<string, unknown>): string {
  const raw = item.Username ?? item.username ?? item.UserName ?? item.userName;
  return raw == null ? '' : String(raw).toLowerCase();
}

/**
 * Extract per-area total wagers from a PlaceBet envelope.
 * Looks for an object keyed by BetType-style names (GLamborghini, etc.).
 * Probes top-level TotalBets first, then inside Data[].
 */
function extractTotalBets(env: Envelope): Record<string, number> | null {
  if (!env) return null;
  const direct = (env.TotalBets ?? env.totalBets) as unknown;
  const fromDirect = coerceTotalsMap(direct);
  if (fromDirect) return fromDirect;

  const data = env.Data;
  if (Array.isArray(data)) {
    for (const raw of data) {
      if (!raw || typeof raw !== 'object') continue;
      const item = raw as Record<string, unknown>;
      const m = coerceTotalsMap(item.TotalBets ?? item.totalBets);
      if (m) return m;
    }
  } else if (data && typeof data === 'object') {
    const o = data as Record<string, unknown>;
    return coerceTotalsMap(o.TotalBets ?? o.totalBets);
  }
  return null;
}

function coerceTotalsMap(raw: unknown): Record<string, number> | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const out: Record<string, number> = {};
  let any = false;
  for (const [k, v] of Object.entries(raw as Record<string, unknown>)) {
    const n = asNumber(v);
    out[k] = n;
    any = true;
  }
  return any ? out : null;
}

// Reference unused constants to silence noUnusedLocals if added later.
export const _UNUSED_BET_ITEM_IDS = BET_ITEM_IDS;
